/**
 * Settings Component Styles
 */

.container {
  padding: var(--spacing-lg);
  max-width: 500px;
  margin: 0 auto;
  min-height: 100vh;
  position: relative;
}

.title {
  font-size: var(--font-size-xxl);
  text-align: center;
  margin-bottom: var(--spacing-xl);
}

/* Sections */
.section {
  background-color: var(--tg-theme-secondary-bg-color);
  border-radius: var(--border-radius);
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-md);
}

.sectionTitle {
  font-size: var(--font-size-lg);
  font-weight: 600;
  margin-bottom: var(--spacing-sm);
}

.sectionHint {
  font-size: var(--font-size-sm);
  color: var(--tg-theme-hint-color);
  margin-bottom: var(--spacing-md);
}

/* Fields */
.field {
  margin-top: var(--spacing-md);
}

.label {
  display: block;
  font-size: var(--font-size-sm);
  color: var(--tg-theme-hint-color);
  margin-bottom: var(--spacing-xs);
}

.select {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-md);
  color: var(--tg-theme-text-color);
  background-color: var(--tg-theme-bg-color);
  border: 1px solid rgba(128, 128, 128, 0.3);
  border-radius: var(--border-radius-sm);
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right var(--spacing-sm) center;
  cursor: pointer;
}

.select:focus {
  outline: none;
  border-color: var(--tg-theme-button-color);
}

.select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Toggle Row */
.toggleRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-sm) 0;
}

.toggleLabel {
  font-size: var(--font-size-md);
  color: var(--tg-theme-text-color);
}

/* Toggle Switch */
.toggle {
  position: relative;
  width: 51px;
  height: 31px;
  background-color: rgba(128, 128, 128, 0.3);
  border-radius: 31px;
  padding: 2px;
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.toggle:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.toggleOn {
  background-color: var(--tg-theme-button-color);
}

.toggleKnob {
  display: block;
  width: 27px;
  height: 27px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggleOn .toggleKnob {
  transform: translateX(20px);
}

/* Segmented Control */
.segmentedControl {
  display: flex;
  gap: 2px;
  background-color: var(--tg-theme-bg-color);
  border-radius: var(--border-radius-sm);
  padding: 2px;
}

.segment {
  flex: 1;
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-sm);
  color: var(--tg-theme-text-color);
  background-color: transparent;
  border-radius: calc(var(--border-radius-sm) - 2px);
  transition: background-color 0.2s ease, color 0.2s ease;
}

.segment:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.segmentActive {
  background-color: var(--tg-theme-button-color);
  color: var(--tg-theme-button-text-color);
}

/* Buttons */
.secondaryButton {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  margin-top: var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--tg-theme-link-color);
  background-color: transparent;
  border: 1px solid var(--tg-theme-link-color);
  border-radius: var(--border-radius-sm);
  transition: background-color 0.2s ease;
}

.secondaryButton:active {
  background-color: rgba(0, 0, 0, 0.05);
}

.secondaryButton:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Info Text */
.infoText {
  font-size: var(--font-size-sm);
  color: var(--tg-theme-hint-color);
  margin-top: var(--spacing-md);
  text-align: center;
}

/* Error Banner */
.errorBanner {
  background-color: var(--tg-theme-destructive-text-color);
  color: white;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-sm);
  margin-bottom: var(--spacing-md);
  font-size: var(--font-size-sm);
  cursor: pointer;
}

/* Loading State */
.loadingContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: var(--spacing-md);
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--tg-theme-secondary-bg-color);
  border-top-color: var(--tg-theme-button-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Error State */
.errorContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: var(--spacing-md);
  text-align: center;
}

.errorText {
  color: var(--tg-theme-destructive-text-color);
  font-size: var(--font-size-md);
}

.retryButton {
  padding: var(--spacing-sm) var(--spacing-lg);
  font-size: var(--font-size-md);
  color: var(--tg-theme-button-text-color);
  background-color: var(--tg-theme-button-color);
  border-radius: var(--border-radius-sm);
}

/* Saving Overlay */
.savingOverlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

/* Collapsible Categories */
.category {
  margin-top: var(--spacing-md);
  background-color: var(--tg-theme-bg-color);
  border-radius: var(--border-radius-sm);
  overflow: hidden;
}

.categoryHeader {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm) var(--spacing-md);
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
}

.categoryHeader:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.categoryTitle {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-md);
  color: var(--tg-theme-text-color);
  font-weight: 500;
}

.chevron {
  display: inline-block;
  font-size: var(--font-size-xs);
  color: var(--tg-theme-hint-color);
  transition: transform 0.2s ease;
}

.chevronExpanded {
  transform: rotate(90deg);
}

.categoryCount {
  font-size: var(--font-size-sm);
  color: var(--tg-theme-hint-color);
  font-weight: 400;
  margin-left: var(--spacing-xs);
}

.categoryContent {
  padding: 0 var(--spacing-md) var(--spacing-sm);
}

/* Subscription Row */
.subscriptionRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-xs) 0;
}

.subscriptionLabel {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-md);
  color: var(--tg-theme-text-color);
}

.subscriptionSymbol {
  font-size: var(--font-size-lg);
  width: 24px;
  text-align: center;
}

/* Smaller toggle for subscription rows */
.toggleSmall {
  width: 44px;
  height: 26px;
}

.toggleSmall .toggleKnob {
  width: 22px;
  height: 22px;
}

.toggleSmall.toggleOn .toggleKnob {
  transform: translateX(18px);
}
